<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';

import { computed } from 'vue';

import TableLoader from '@/components/loaders/TableLoader.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';
import { TomorrowTobeSchedReportData } from '@/models/reports';

import tableStyles from '@/styles/table.module.scss';

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<TomorrowTobeSchedReportData[]>;
  date?: string;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
  // TODO maybe don't show data when date is undefined
  date: undefined,
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<TomorrowTobeSchedReportData[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'rockhopper_id',
      title: 'Id',
      isNotSortable: true,
    },
    {
      value: 'name',
      title: 'Name',
      isNotSortable: true,
    },
    {
      value: 'suburb',
      title: 'Suburb',
      isNotSortable: true,
    },
    {
      value: 'departed_at',
      title: 'Departed at',
      isNotSortable: true,
    },
    {
      value: 'split_to_base',
      title: 'Split to base',
      isNotSortable: true,
    },
    {
      value: 'scheduled',
      title: 'Scheduled',
      isNotSortable: true,
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length;
});
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.hovered,
      $style.tomorrowToBeScheduledReportTable,
    ]"
    data-testid="tomorrowToBeScheduledReportTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.rockhopper_id">
        <tr>
          <td>
            <JobIdAndIconLinkUnit
              :job-id="parseInt(dataItem.rockhopper_id)"
              :job-date="props.date"
            />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.name" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.suburb" />
          </td>
          <td>
            <EmDashPlaceholder :item="dataItem.departed_at" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.split_to_base" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.scheduled" />
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.tomorrowToBeScheduledReportTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
    & > th {
      position: sticky;
      top: variables.$appBarHeight;
      z-index: 1;
    }
  }
}
</style>
